import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '@/lib/api';

const AuthCtx = createContext({});
export const useAuth = () => useContext(AuthCtx);

const TOKEN_KEY = 'kba_token';
const USER_KEY = 'kba_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading] = useState(false);

  useEffect(() => {
    // re-validate token on mount only — deliberately not re-run when
    // token/user change afterwards (login/logout already update them directly).
    if (token && !user) {
      api.get('/auth/me').then((r) => {
        setUser(r.data);
        localStorage.setItem(USER_KEY, JSON.stringify(r.data));
      }).catch(() => logout());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (newToken, newUser) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  };

  const updateUser = (updatedFields) => {

  const updatedUser = {
    ...user,
    ...updatedFields,
  };

  localStorage.setItem(
    USER_KEY,
    JSON.stringify(updatedUser)
  );

  setUser(updatedUser);
};

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    persist(data.token, data.user);
    return data.user;
  };

  const adminLogin = async (email, password) => {
    const { data } = await api.post('/auth/admin/login', { email, password });
    persist(data.token, data.user);
    return data.user;
  };

  const register = async (payload) => {
    // NEW: email verification. The backend no longer logs the new account in
    // immediately — it creates the account (unverified) and emails a
    // verification link. There is no token/user to persist here anymore;
    // the caller shows a "check your email" state instead of navigating in.
    const { data } = await api.post('/auth/register', payload);
    return data;
  };

  const verifyEmail = async (token) => {
    const { data } = await api.post('/auth/verify-email', { token });
    return data;
  };

  const resendVerification = async (email) => {
    const { data } = await api.post('/auth/resend-verification', { email });
    return data;
  };

  const forgotPassword = async (email) => {

  const { data } = await api.post(
    '/auth/forgot-password',
    { email }
  );

  return data;
};

const resetPassword = async (
  token,
  newPassword
) => {

  const { data } = await api.post(
    '/auth/reset-password',
    {
      token,
      new_password: newPassword,
    }
  );

  return data;
};

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  };

  const googleLogin = async (googleToken) => {
  const { data } = await api.post('/auth/google', {
    token: googleToken,
  });

  persist(data.token, data.user);

  return data.user;
};

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, adminLogin, register, verifyEmail, resendVerification, logout, googleLogin, forgotPassword, resetPassword, updateUser }}>
      {children}
    </AuthCtx.Provider>
  );
};
